/**
 * The reasoning layer's evaluation (spec account-persona 09 §5.5, owner decision D8): the thresholds and the weekly
 * schedule, the call funnel of each followed source (where findings drop), and every run with its checks.
 * Template example: NextAdmin `(dashboard)/(home)/page.tsx` (overview stats, then a grid of cards and a table).
 */
import type React from 'react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, FlaskConical, ListChecks, XCircle } from 'lucide-react';
import {
  getReasoningCallSummaryOptions,
  getReasoningPrincipleSummaryOptions,
  listReasoningEvaluationsOptions,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { ReasoningEvaluation } from '../../generated/api/types.gen.js';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  ItemList,
  Skeleton,
} from '../../components/ui/index.js';
import { DashboardGrid, DashboardPage } from '../../components/page/DashboardPage.js';
import { PAGE_HELP } from '../../help/index.js';
import { ReasoningCallFunnelCard } from './reasoning/ReasoningCallFunnelCard.js';
import { ReasoningFindingsFunnelCard } from './reasoning/ReasoningFindingsFunnelCard.js';
import { ReasoningPrincipleFunnelCard } from './reasoning/ReasoningPrincipleFunnelCard.js';
import { ReasoningRunSheet } from './reasoning/ReasoningRunSheet.js';
import { askedOn, percent } from './reasoning/reasoning-format.js';

const columns: DataTableColumn<ReasoningEvaluation>[] = [
  {
    key: 'ran',
    header: 'Ran',
    render: (run) => (
      <div className="min-w-0">
        <div className="font-medium text-heading">{run.handle ?? 'Unknown source'}</div>
        <div className="text-xs text-muted">{new Date(run.createdAt).toLocaleString()}</div>
      </div>
    ),
  },
  { key: 'asked', header: 'Asked on', render: (run) => askedOn(run.surface, run.policyId) },
  {
    key: 'questions',
    header: 'Answered',
    render: (run) => (run.report ? `${run.report.answered} / ${run.report.asked}` : '—'),
  },
  {
    key: 'direction',
    header: 'Direction',
    render: (run) => (run.report ? percent(run.report.directionFidelity.share) : '—'),
  },
  { key: 'readings', header: 'Own readings', render: (run) => (run.report ? percent(run.report.readingF1.mean) : '—') },
  {
    key: 'facts',
    header: 'Readings held',
    render: (run) => (run.report ? percent(run.report.factValidity.share) : '—'),
  },
  { key: 'quotes', header: 'Unfound quotes', render: (run) => run.report?.unfoundQuotes ?? '—' },
  {
    key: 'checks',
    header: 'Checks',
    render: (run) =>
      run.failedChecks > 0 ? (
        <Badge variant="danger">{run.failedChecks} below threshold</Badge>
      ) : run.checks.length > 0 ? (
        <Badge variant="success">All met</Badge>
      ) : (
        <Badge variant="neutral">Not judged</Badge>
      ),
  },
];

export const ReasoningEvaluationsPage: React.FC = () => {
  const help = PAGE_HELP['reasoning-evaluations'];
  const [open, setOpen] = useState<ReasoningEvaluation | null>(null);
  const runs = useQuery(listReasoningEvaluationsOptions({ query: { limit: 50 } }));
  const funnels = useQuery(getReasoningCallSummaryOptions());
  const principles = useQuery(getReasoningPrincipleSummaryOptions());
  const rows = runs.data?.data ?? [];
  const latest = rows[0];
  const failed = latest?.failedChecks ?? 0;
  const failedLoad = runs.isError || funnels.isError || principles.isError;

  return (
    <DashboardPage
      title="Reasoning evaluations"
      subtitle="How faithfully each account persona reasons like its account, where calls and reasons are dropped before they are measured, and how its principles fare against the data."
      breadcrumbs={[
        { label: 'Libraries', to: '/agents/libraries' },
        { label: 'Evaluations', to: '/admin/evaluations' },
        { label: 'Reasoning' },
      ]}
      helpContent={help}
      stats={
        runs.isPending
          ? undefined
          : [
              { label: 'Runs', value: rows.length, icon: FlaskConical },
              {
                label: 'Latest run',
                value: latest ? new Date(latest.createdAt).toLocaleDateString() : '—',
                sub: latest?.handle ?? undefined,
                icon: ListChecks,
              },
              {
                label: 'Checks below threshold (latest)',
                value: failed,
                icon: XCircle,
                severity: failed > 0 ? 'danger' : undefined,
              },
              { label: 'Schedule', value: 'Weekly', sub: runs.data?.meta.schedule, icon: CalendarClock },
            ]
      }
      alert={
        failedLoad ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load the reasoning evaluations"
            actions={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  void runs.refetch();
                  void funnels.refetch();
                  void principles.refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Check your connection and try again.
          </Callout>
        ) : undefined
      }
    >
      {!failedLoad && (
        <DashboardGrid>
          {funnels.isPending ? (
            <Card title="Call funnel" className="xl:col-span-6">
              <Skeleton className="h-64" />
            </Card>
          ) : (
            (funnels.data?.sources ?? []).map((funnel) => (
              <ReasoningCallFunnelCard key={funnel.intelSourceId} funnel={funnel} />
            ))
          )}
          {principles.isPending ? (
            <Card title="Principle funnel" className="xl:col-span-6">
              <Skeleton className="h-64" />
            </Card>
          ) : (
            (principles.data?.sources ?? []).flatMap((funnel) => [
              <ReasoningPrincipleFunnelCard key={`principles-${funnel.intelSourceId}`} funnel={funnel} />,
              ...(funnel.module
                ? [
                    <ReasoningFindingsFunnelCard
                      key={`findings-${funnel.intelSourceId}`}
                      handle={funnel.handle}
                      calls={funnels.data?.sources.find((calls) => calls.intelSourceId === funnel.intelSourceId)}
                      principles={funnel}
                      count={funnel.module}
                    />,
                  ]
                : []),
            ])
          )}
          <Card title="Thresholds" className="xl:col-span-6">
            <ItemList
              items={(runs.data?.meta.rules ?? []).map((rule) => ({
                id: rule.key,
                title: rule.label,
                subtitle: rule.reason,
                value: rule.rule,
              }))}
            />
          </Card>
          <div className="xl:col-span-12">
            <DataTable
              framed
              aria-label="Reasoning evaluation runs"
              columns={columns}
              rows={rows}
              rowKey={(run) => run.id}
              loading={runs.isPending}
              onRowClick={(run) => setOpen(run)}
              pageSize={20}
              empty={
                <EmptyState
                  icon={FlaskConical}
                  title={help.emptyState.title}
                  description={help.emptyState.description}
                  learnItems={help.emptyState.learnItems}
                />
              }
            />
          </div>
        </DashboardGrid>
      )}
      <ReasoningRunSheet run={open} onClose={() => setOpen(null)} />
    </DashboardPage>
  );
};

export default ReasoningEvaluationsPage;
